import React, { useEffect, useRef, useState } from 'react';
import { NavBar } from '../kuroco/AppBars';
import { ChatBanner, ChatInput } from '../kuroco/ChatChrome';
import { AiBubble, Chips, DateDivider, UserBubble } from '../kuroco/ChatPrimitives';
import { fetchGoals, saveActionCheckin } from '../../api/goalsApi';
import { notifyGoalsUpdated } from '../../utils/goalsLocalStore';
import { useCanEditStylist } from '../../hooks/useUserRole';
import { CHECKIN_MEMO_MAX } from '../../constants/checkinReasons';

function Question({ index, action, answers, reasons, reasonOpts, memos, memoDone, onAnswer, onReason, onMemoNext }) {
  const ans = answers[index];
  return (
    <>
      <AiBubble
        html={`<strong>${['①', '②', '③'][index]}「${action.name}」</strong><br>${action.cadence} —— できた？できなかった？`}
      />
      <div className="checkin">
        <button
          type="button"
          className={`checkin-btn${ans === 'できた' ? ' is-yes' : ''}`}
          onClick={() => onAnswer(index, 'できた')}
        >
          <span className="checkin-btn-ic">✅</span>
          <span className="checkin-btn-l">できた</span>
        </button>
        <button
          type="button"
          className={`checkin-btn${ans === 'できなかった' ? ' is-no' : ''}`}
          onClick={() => onAnswer(index, 'できなかった')}
        >
          <span className="checkin-btn-ic">✕</span>
          <span className="checkin-btn-l">できなかった</span>
        </button>
      </div>
      {ans && <UserBubble text={ans} />}
      {ans === 'できなかった' && (
        <>
          <AiBubble html="そうだったんですね。差し支えなければ、<strong>できなかった理由</strong>を教えてください。" />
          <div className="chips">
            {reasonOpts.map((r) => (
              <button
                type="button"
                key={r.label}
                className={`chip${reasons[index] === r.label ? ' chip--primary' : ''}`}
                onClick={() => onReason(index, r.label)}
              >
                {r.label}
              </button>
            ))}
          </div>
          {reasons[index] && <UserBubble text={reasons[index]} />}
          {reasons[index] && !memoDone[index] && (
            <>
              <AiBubble html="ひと言あれば、下の入力欄から送ってください（<strong>任意</strong>です）。" />
              <div className="chips">
                <button type="button" className="chip" onClick={() => onMemoNext(index)}>
                  なしで次へ
                </button>
              </div>
            </>
          )}
          {memoDone[index] && (memos[index] || '').trim() && <UserBubble text={memos[index].trim()} />}
          {memoDone[index] && <AiBubble>回答ありがとうございます。</AiBubble>}
        </>
      )}
    </>
  );
}

function buildEntries(actions, ans, reasons, memos = []) {
  return actions.map((a, i) => ({
    actionId: a.id ?? i + 1,
    actionName: a.name,
    status: ans[i] === 'できた' ? 'done' : 'miss',
    reason: ans[i] === 'できなかった' ? reasons[i] || null : null,
    memo: ans[i] === 'できなかった' ? (memos[i] || '').trim() || null : null,
  }));
}

export default function AiRecord({ data, onNavigate, settingsPath, store, stylist }) {
  const t = data.dailyReview;
  const actions = (data.actions || []).slice(0, 3);
  const [ans, setAns] = useState([null, null, null]);
  const [reasons, setReasons] = useState([null, null, null]);
  const [memos, setMemos] = useState(['', '', '']);
  const [memoDone, setMemoDone] = useState([false, false, false]); // ひと言 step closed (text or not)
  const canEditGoals = useCanEditStylist(stylist); // own record only — see goals_service.can_write_stylist_goals
  const [saveState, setSaveState] = useState('idle'); // idle|saving|saved|error
  const [saveError, setSaveError] = useState(null);
  const savedKeyRef = useRef(null);
  // See InlineRecordFlow (AiHome.jsx): `actions` is a fresh array every render, so it must
  // not be an effect dependency, and the in-flight key is set before the request.
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  const cleared = (i) => ans[i] === 'できた' || (ans[i] === 'できなかった' && reasons[i] && memoDone[i]);
  const done = actions.length > 0 && actions.every((_, i) => cleared(i));
  const anyNo = ans.includes('できなかった');

  const setAnswer = (i, v) => setAns((a) => {
    const n = [...a];
    n[i] = v;
    return n;
  });
  const setReason = (i, v) => setReasons((r) => {
    const n = [...r];
    n[i] = v;
    return n;
  });
  const setMemo = (i, v) => setMemos((m) => { const n = [...m]; n[i] = v; return n; });
  const closeMemo = (i) => setMemoDone((d) => { const n = [...d]; n[i] = true; return n; });
  const memoWaitIndex = actions.findIndex((_, i) => ans[i] === 'できなかった' && reasons[i] && !memoDone[i]);

  useEffect(() => {
    if (!done || !store || !stylist || !canEditGoals) return;
    const key = JSON.stringify({ store, stylist, ans, reasons, memos });
    if (savedKeyRef.current === key) return;
    savedKeyRef.current = key; // in-flight marker, cleared on failure
    const actionsNow = actionsRef.current;
    let cancelled = false;
    (async () => {
      setSaveState('saving');
      setSaveError(null);
      try {
        await saveActionCheckin({
          store,
          stylist,
          entries: buildEntries(actionsNow, ans, reasons, memos),
          source: 'ai_record_ui',
        });
        try {
          const goalsRes = await fetchGoals({ store, stylist });
          if (goalsRes?.goals) notifyGoalsUpdated(goalsRes.goals);
        } catch {
          /* check-in saved; Goals rates may lag */
        }
        if (!cancelled) setSaveState('saved');
      } catch (err) {
        if (savedKeyRef.current === key) savedKeyRef.current = null;
        if (!cancelled) {
          setSaveState('error');
          setSaveError(err.message || '保存に失敗しました');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [done, store, stylist, ans, reasons, memos, canEditGoals]);

  const retrySave = async () => {
    if (!canEditGoals) return;
    setSaveState('saving');
    setSaveError(null);
    try {
      await saveActionCheckin({
        store,
        stylist,
        entries: buildEntries(actions, ans, reasons),
        source: 'ai_record_ui',
      });
      try {
        const goalsRes = await fetchGoals({ store, stylist });
        if (goalsRes?.goals) notifyGoalsUpdated(goalsRes.goals);
      } catch {
        /* check-in saved; Goals rates may lag */
      }
      savedKeyRef.current = JSON.stringify({ store, stylist, ans, reasons });
      setSaveState('saved');
    } catch (err) {
      setSaveState('error');
      setSaveError(err.message || '保存に失敗しました');
    }
  };

  return (
    <div className="ai-screen">
      <NavBar title="行動を記録する" backLabel="AI相談" onBack={() => onNavigate('home')} settingsPath={settingsPath} />
      <ChatBanner icon="📝" title="今日の行動を記録" />
      <div className="chat-scroll fade">
        <DateDivider label={data.dateDividerLabel} />
        <AiBubble
          html={`${data.stylistName}さん、<strong>${t.shortDate}</strong>の3つの行動を振り返りましょう。<br><strong>できた／できなかった</strong> をタップするだけ。`}
        />
        {actions[0] && (
          <Question
            index={0}
            action={actions[0]}
            answers={ans}
            reasons={reasons}
            reasonOpts={t.reasons}
            memos={memos}
            memoDone={memoDone}
            onAnswer={setAnswer}
            onReason={setReason}
            onMemoNext={closeMemo}
          />
        )}
        {cleared(0) && actions[1] && (
          <Question
            index={1}
            action={actions[1]}
            answers={ans}
            reasons={reasons}
            reasonOpts={t.reasons}
            memos={memos}
            memoDone={memoDone}
            onAnswer={setAnswer}
            onReason={setReason}
            onMemoNext={closeMemo}
          />
        )}
        {cleared(0) && cleared(1) && actions[2] && (
          <Question
            index={2}
            action={actions[2]}
            answers={ans}
            reasons={reasons}
            reasonOpts={t.reasons}
            memos={memos}
            memoDone={memoDone}
            onAnswer={setAnswer}
            onReason={setReason}
            onMemoNext={closeMemo}
          />
        )}
        {done && (
          <>
            <div className="recap">
              <div className="recap-head">
                {saveState === 'saving' && '⏳ 保存中…'}
                {saveState === 'saved' && '✅ 記録しました'}
                {saveState === 'error' && '⚠ 保存に失敗しました'}
                {saveState === 'idle' && '✅ 記録しました'}
              </div>
              {saveError && (
                <div className="recap-reason">
                  {saveError}
                  {' '}
                  <button type="button" className="chip primary" onClick={retrySave}>再試行</button>
                </div>
              )}
              {actions.map((a, i) => (
                <React.Fragment key={a.id ?? i}>
                  <div className="recap-row">
                    <span className="name">
                      <span className="num">{a.id ?? i + 1}</span>
                      {a.name}
                    </span>
                    <span className={ans[i] === 'できなかった' ? 'ans-no' : 'ans-yes'}>
                      {ans[i]}
                      {ans[i] === 'できなかった' ? ' ⚠' : ''}
                    </span>
                  </div>
                  {ans[i] === 'できなかった' && reasons[i] && (
                    <div className="recap-reason">理由：{reasons[i]}</div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <AiBubble
              html={
                ans[1] === 'できなかった'
                  ? `今日のボトルネックは <strong>②</strong>。明日は1人だけ声をかけてみましょう。`
                  : anyNo
                    ? 'できなかった行動も、理由がわかれば次に活かせます。'
                    : '今日は3つとも積み重ねできましたね。'
              }
            />
            <Chips
              onNavigate={onNavigate}
              items={[
                { label: '💡 ②の声がけ例を聞く', route: 'improve' },
                { label: '📊 数字を振り返る', route: 'review' },
              ]}
            />
          </>
        )}
      </div>
      {/* ひと言 goes through this input bar (owner 2026-10-26): enabled only while an action waits for one */}
      <ChatInput
        placeholder={memoWaitIndex >= 0 ? 'ひと言を入力（任意）…' : 'メモを入力…'}
        onSend={(text) => {
          if (memoWaitIndex < 0) return;
          setMemo(memoWaitIndex, String(text || '').trim().slice(0, CHECKIN_MEMO_MAX));
          closeMemo(memoWaitIndex);
        }}
        disabled={memoWaitIndex < 0}
      />
    </div>
  );
}
